import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, MessageCircle, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Lightning } from "@/components/ui/hero-odyssey";
import emailjs from "@emailjs/browser";

const Contact = () => {
  const { toast } = useToast();

  useEffect(() => {
    if (window.location.hash === "#thunder-fox-contact") {
      const element = document.getElementById("thunder-fox-contact");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    emailjs
      .send(
        "service_ffn34gf",
        "template_3tegh29",
        {
          from_name: formData.name,
          reply_to: formData.email,
          time: new Date().toLocaleString(),
          message: formData.message,
        },
        "O4XPWefBATbn0YXC5"
      )
      .then(() => {
        toast({
          title: "Message Sent! ⚡",
          description: "We'll get back to you lightning-fast!",
        });
        setFormData({ name: "", email: "", message: "" });
        setLoading(false);
      })
      .catch(() => {
        toast({
          title: "Error",
          description: "Something went wrong. Please try again.",
          variant: "destructive",
        });
        setLoading(false);
      });
  };

  return (
    <main id="thunder-fox-contact" className="min-h-screen py-20 relative">
      {/* Background Animation */}
      <div className="fixed inset-0 z-0">
        <Lightning hue={220} xOffset={0} speed={1.6} intensity={0.6} size={2} />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl font-bold text-foreground mb-6">
            Let's Build Something{" "}
            <span className="text-primary neon-glow">Powerful</span> Together ⚡
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Have a project in mind? We'd love to hear from you. Get in touch and let's create something amazing!
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="glassmorphism-dark rounded-2xl p-8 border border-primary/30 electric-border-animated">
              <h2 className="text-2xl font-bold text-foreground mb-6">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                    Name
                  </label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-background border-primary/30"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                    Email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="bg-background border-primary/30"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    placeholder="Tell us about your project..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={5}
                    className="bg-background border-primary/30"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-primary-foreground electric-glow hover:bg-primary/90 transition-all duration-300 ease-in-out"
                >
                  {loading ? "Sending..." : <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>}
                </Button>
              </form>
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="space-y-8"
          >
            {/* Contact Details */}
            <div className="glassmorphism-dark rounded-2xl p-8 border border-primary/30 electric-border-animated">
              <h2 className="text-2xl font-bold text-foreground mb-6">Contact Information</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-primary/10 rounded-lg electric-glow">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Location</h3>
                    <p className="text-muted-foreground">Cuddalore, Tamil Nadu, India</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-primary/10 rounded-lg electric-glow">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Email</h3>
                    <a
                      href="mailto:thunderfoxweb@gmail.com"
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      thunderfoxweb@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-primary/10 rounded-lg electric-glow">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Phone</h3>
                    <p className="text-muted-foreground">+91 73390 10167</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="glassmorphism-dark rounded-2xl p-8 border border-primary/30 electric-border-animated">
              <h2 className="text-2xl font-bold text-foreground mb-6">Connect With Us</h2>
              <div className="flex space-x-4">
                <a
                  href="https://wa.me/917339010167?text=Hi%20Thunder%20Fox%20Web%20Solutions,%20I%20would%20like%20to%20know%20more%20about%20your%20services." 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 p-4 bg-muted rounded-lg hover:bg-primary hover:text-primary-foreground transition-all electric-border text-center"
                >
                  <MessageCircle className="w-6 h-6 mx-auto mb-2" />
                  <span className="text-sm font-medium">WhatsApp</span>
                </a>

                <a
                  href="https://linkedin.com/in/thunder-fox-web-solutions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 p-4 bg-muted rounded-lg hover:bg-primary hover:text-primary-foreground transition-all electric-border text-center"
                >
                  <Linkedin className="w-6 h-6 mx-auto mb-2" />
                  <span className="text-sm font-medium">LinkedIn</span>
                </a>
              </div>
            </div>

            {/* Certifications */}
            <div className="glassmorphism-dark rounded-2xl p-8 border border-primary/30 electric-border-animated">
              <h3 className="text-xl font-bold text-foreground mb-4">Certifications</h3>
              <div className="space-y-2">
                <p className="text-primary font-semibold">✓ MSME Registered</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </main>
  );
};

export default Contact;
